import { useContext, useEffect, useState } from 'react'
import { TextField } from '@nv/basic-components'
import { PhoneInput } from '@nv/components'
import { ModalContext } from '@nv/contexts'
import WarningIcon from '@nv/images/design/warning-icon'
import { AdminVoterService } from '@nv/services'
import { EMAIL_REGEX } from '@nv/utils'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import styles from './EditUser.module.scss'

const initialValues = {
  email: '',
  phone: '',
}

export const EditUser = ({ userToEdit, modalView, onUpdate }) => {
  const { t } = useTranslation()
  const [updateErr, setUpdateErr] = useState(false)
  const [loading, setLoading] = useState(false)
  const { setHeaderLabel, setBtns, onClose } = useContext(ModalContext)

  const schema = Yup.object().shape({
    email: Yup.string().matches(EMAIL_REGEX, t('form.validations.emailFormat')).required(t('form.validations.email')),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true)
      let response
      const { voterId, keycloakId } = userToEdit
      if (modalView === 'Reset password') {
        response = await AdminVoterService.resetVoterPassword({ keycloakId })
      } else {
        response = await AdminVoterService.updateVoter({
          voterId,
          email: values.email,
          phone: values.phone,
          updateType: modalView === 'Edit' ? 'edit' : 'email',
        })
      }
      setLoading(false)

      if (response.success) {
        onUpdate()
        onClose()
      } else setUpdateErr(true)
    },
  })

  useEffect(() => {
    formik.setValues({
      email: userToEdit.email,
      phone: userToEdit.phone,
    })
  }, [])

  useEffect(() => {
    if (userToEdit.votingChannel === 'inPerson') {
      setHeaderLabel(t('admin.electorsDetails.editForbid'))
      setBtns([{ text: t('buttons.ok'), onClick: onClose }])
    } else if (!userToEdit.keycloakId || (userToEdit.voterInfo && userToEdit.voterInfo.status !== 'Approved')) {
      setHeaderLabel(t('admin.electorsDetails.userNotApproved'))
      setBtns([{ text: t('buttons.ok'), onClick: onClose }])
    } else {
      setHeaderLabel(
        modalView === 'Edit'
          ? t('admin.electorsDetails.edit', { name: userToEdit.username })
          : t('admin.electorsDetails.resendEmail', { name: userToEdit.username }),
      )
      setBtns([
        {
          text: t('buttons.cancel'),
          onClick: onClose,
          variant: 'cancel',
        },
        {
          text:
            modalView === 'Edit'
              ? t('admin.users.actions.edit')
              : modalView === 'Reset password'
                ? t('admin.users.actions.resetPassword')
                : t('admin.users.actions.emailAccess'),
          onClick: () => formik.submitForm(),
          disabled: loading,
        },
      ])
    }
  }, [loading])

  if (userToEdit.votingChannel === 'inPerson') {
    return (
      <div className={styles['container']}>
        <WarningIcon color={'var(--error-color)'} />
        <span className={styles['error__text']}>{t('admin.electorsDetails.editForbid')}</span>
      </div>
    )
  }

  if (!userToEdit.keycloakId || (userToEdit.voterInfo && userToEdit.voterInfo.status !== 'Approved')) {
    return (
      <div className={styles['container']}>
        <WarningIcon color={'var(--error-color)'} />
        <span className={styles['error__text']}>{t('admin.electorsDetails.userNotApproved')}</span>
      </div>
    )
  }

  return (
    <div className={styles['container']}>
      <TextField
        formik={formik}
        type='email'
        field='email'
        {...formik.getFieldProps('email')}
        placeholder={t('form.labels.email')}
        label={t('form.labels.email')}
        isRequired={true}
        disabled={modalView !== 'Edit'}
        autoComplete='off'
      />
      <PhoneInput formik={formik} field='phone' autoComplete='off' disabled={modalView !== 'Edit'} />

      {updateErr && (
        <span className={styles['error__text']} role='alert'>
          {t('admin.electorsDetails.editError')}
        </span>
      )}
    </div>
  )
}
